/**
 * Auth Profile Override Toggle (Phase 9)
 * Allows profile to override web target's default update behavior
 * Used in AuthProfile editor/form
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff, faBolt, faQuestion, faPlus, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const AuthProfileOverrideToggle = ({ profile, webTargetDefaultMode = 'auto', onChange, isDarkMode }) => {
  const override = profile?.updateBehaviorOverride || { enabled: false, mode: 'auto' };

  const handleToggle = () => {
    onChange({
      ...override,
      enabled: !override.enabled,
      mode: override.mode || webTargetDefaultMode
    });
  };

  const handleModeChange = (mode) => {
    onChange({
      ...override,
      mode
    });
  };

  return (
    <div className={`space-y-3 p-4 rounded-lg border ${
      isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
    }`}>
      {/* Toggle header */}
      <div className="flex items-center justify-between">
        <div>
          <label className={`text-sm font-medium ${
            isDarkMode ? 'text-gray-200' : 'text-gray-700'
          }`}>
            Override Update Behavior
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            Override web target's default mode for this profile only
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
            override.enabled
              ? 'bg-blue-500 text-white'
              : isDarkMode
              ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
        >
          <FontAwesomeIcon icon={override.enabled ? faToggleOn : faToggleOff} className="text-lg" />
          <span className="text-sm font-medium">
            {override.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </button>
      </div>

      {/* Show default mode when disabled */}
      {!override.enabled && (
        <div className={`text-xs p-2 rounded ${
          isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-white text-gray-600'
        }`}>
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          Using web target default:
          <span className="font-medium ml-1">
            {webTargetDefaultMode === 'auto' && '⚡ Auto-update'}
            {webTargetDefaultMode === 'ask' && '❓ Ask first'}
            {webTargetDefaultMode === 'create_new' && '➕ Create new'}
          </span>
        </div>
      )}

      {/* Mode selector when enabled */}
      {override.enabled && (
        <div className="space-y-2 pt-2">
          {/* Auto mode */}
          <label className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
            override.mode === 'auto'
              ? isDarkMode
                ? 'bg-blue-900/30 border border-blue-700/50'
                : 'bg-blue-50 border border-blue-200'
              : isDarkMode
              ? 'hover:bg-gray-700/30'
              : 'hover:bg-gray-100'
          }`}>
            <input
              type="radio"
              name="overrideMode"
              value="auto"
              checked={override.mode === 'auto'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FontAwesomeIcon icon={faBolt} className="text-blue-500 text-xs" />
                <span>Auto-update</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Silently update when this user logs in again
              </p>
            </div>
          </label>

          {/* Ask mode */}
          <label className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
            override.mode === 'ask'
              ? isDarkMode
                ? 'bg-blue-900/30 border border-blue-700/50'
                : 'bg-blue-50 border border-blue-200'
              : isDarkMode
              ? 'hover:bg-gray-700/30'
              : 'hover:bg-gray-100'
          }`}>
            <input
              type="radio"
              name="overrideMode"
              value="ask"
              checked={override.mode === 'ask'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FontAwesomeIcon icon={faQuestion} className="text-yellow-500 text-xs" />
                <span>Ask first</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Show confirmation modal before updating
              </p>
            </div>
          </label>

          {/* Create new mode */}
          <label className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
            override.mode === 'create_new'
              ? isDarkMode
                ? 'bg-blue-900/30 border border-blue-700/50'
                : 'bg-blue-50 border border-blue-200'
              : isDarkMode
              ? 'hover:bg-gray-700/30'
              : 'hover:bg-gray-100'
          }`}>
            <input
              type="radio"
              name="overrideMode"
              value="create_new"
              checked={override.mode === 'create_new'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FontAwesomeIcon icon={faPlus} className="text-green-500 text-xs" />
                <span>Create new</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Always create new profile on re-login
              </p>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default AuthProfileOverrideToggle;
